/*
 * Description:
 * ---------------------
 * Every sentence the Approvers, approval and Agent Rules screens show
 * (ApproversPanel, ApprovalPage, NeedsYou, AgentRulesPanel), as pure
 * functions of what notabox-server answers at /api/v1/mesh/approvers,
 * /mesh/approvals, /mesh/freeze and /mesh/rules (server/pkg/approvals).
 * Things are named as the Trust Fabric names them: a mandate is a key card, a
 * box's record its logbook, the catalogue the menu.
 *
 * What an approver is shown (authority.Rendering for a key card,
 * ApproversRendering for a new approver list, RulesRendering for new rules)
 * is shown line for line, in its order and from its text alone: these words
 * give each line a plain label and say what its value means, with the value
 * as signed beside them wherever the two differ, and add nothing the text
 * does not say. A line whose key they do not know keeps its own key as its
 * label, and the rules' own text after "policies:" is shown as written, so
 * nothing the passkey signs is left off.
 *
 * ! file components/approvalWords.js
 */
import { at, hm, list, reachWords, signerWords } from "./TrustFabricLayout";
import { by as taskBy } from "./meshWords";
import { roleLabel } from "./accessWords";

export const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
// Only a sentence that starts with a word of ours is capitalised: a box or a
// person keeps the name it was given, as on the Trust Fabric.
const capArticle = (s) => s.replace(/^(the|a|an|your) /, (m) => cap(m));
// A name with an aside of its own ("the fleet agent, through Weavian’s
// console") closes it before a verb.
const subject = (s) => (s.includes(", ") ? `${s},` : s);
const plural = (k, one, many) => `${k} ${k === 1 ? one : many}`;
const times = (k) => (k === 1 ? "once" : k === 2 ? "twice" : `${k} times`);
const valid = (iso) => !!iso && !Number.isNaN(Date.parse(iso));
const day = (iso) => new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
const sameDay = (a, b) => new Date(a).toDateString() === new Date(b).toDateString();
/* A time a person acts on: the clock alone today, the day too otherwise. */
const clock = (iso, now) => (sameDay(iso, now) ? `${hm(iso)} today` : at(iso, now));
/* A person on an approver list, by the username their name carries. */
const person = (name) => signerWords(name);

export const APPROVERS_HERO = "The people whose yes an AI agent needs before a big change, and the passkeys they say it with.";
export const RULES_HERO = "The rules every box judges an AI agent’s requests by, and the switch that stops every agent change at once.";
export const NEEDS_HERO = "Changes an AI agent asks to make, waiting for your yes.";
export const NOT_ON_SERVER = "The server runs a build from before this screen shipped. It appears here once the server is updated; nothing else in the console is affected.";
export const CUSTOMER = "Organisation";

/* ── What an approver is shown, line for line ──────────────────────────── */

const LABEL = {
  agent: "Who", customer: "For", box: "Where", entry: "What", kind: "Covers", arguments: "Only with",
  ceiling: "Reaching at most", from: "From", until: "Until", wave: "Rollout", catalogue: "Menu version",
  actions: "How many times", delegable: "May hand it on to",
  plane: "Control plane", serial: "List", origin: "Approved at", rp_id: "Passkeys made for", approver: "Approver", passkey: "Their passkey",
  version: "Version", mode: "Mode", expires: "Runs until", defaults: "Narrows the defaults", policies: "The rules",
};
const MODES = {
  log: "Log mode: every box writes down what they would refuse, and refuses nothing",
  enforce: "Enforced: every box refuses what they do not allow",
};

const parseEntry = (v) => ({ id: v.trim().split(/[\s(]/)[0], cls: (/\bB[0-4]\b/.exec(v) || [""])[0] });
/* A catalogue entry in words, from its line alone: what its blast class
   reaches, and the entry by name. */
const changeWords = ({ id, cls }) => (!cls ? `the change ${id}` : cls === "B0" ? `a reading (${id})` : `a change to ${reachWords(cls)} (${id})`);

/* An argument constraint in words, when it is one a box decides on its own
   (an enumeration, a fixed value, a numeric range); otherwise as signed. */
const shown = (x) => (typeof x === "string" ? x : JSON.stringify(x));
function constraintWords(v) {
  const i = v.indexOf("{");
  if (i < 0) return v;
  let schema;
  try { schema = JSON.parse(v.slice(i)); } catch { return v; }
  const props = Object.entries(schema?.properties || {});
  if (!props.length) return v;
  const words = props.map(([name, p]) => {
    if (Array.isArray(p.enum)) return `${name} must be ${p.enum.map(shown).join(" or ")}`;
    if ("const" in p) return `${name} must be ${shown(p.const)}`;
    if ("minimum" in p && "maximum" in p) return `${name} from ${p.minimum} to ${p.maximum}`;
    if ("minimum" in p) return `${name} at least ${p.minimum}`;
    if ("maximum" in p) return `${name} at most ${p.maximum}`;
    return null;
  });
  return words.includes(null) ? v : cap(words.join("; "));
}

const KINDS = { observe: "Every kind of reading", diagnose: "Every kind of test", act: "Every kind of change" };
const WAVES = { canary: "Weavian’s own boxes first", lead: "One site first", rest: "The remaining sites" };
function waveWords(v) {
  if (v === "task") return "This task alone, not a rollout";
  const m = /^(canary|lead|rest)-(\d+)$/.exec(v);
  return m ? `${WAVES[m[1]]}, batch ${m[2]}` : v;
}

function plainOf(key, v, a, now) {
  switch (key) {
    case "agent": case "delegable": return capArticle(signerWords(v));
    case "entry": return cap(changeWords(parseEntry(v)));
    case "kind": return KINDS[v] || v;
    case "arguments": return constraintWords(v);
    case "ceiling": return v === "B0" ? "Reads only" : cap(reachWords(v));
    case "from": case "until": case "expires": return valid(v) ? `${clock(v, now)}, your time` : v;
    case "mode": return MODES[v] || v;
    case "wave": return waveWords(v);
    case "actions": return v === "1" ? "Once" : Number(v) > 1 ? `Up to ${v} times` : v;
    case "approver": { const [name, ...role] = v.split(" "); return `${person(name)}${role.length ? `, as ${roleLabel(role.join(" "))}` : ""}`; }
    case "passkey": return `A passkey (${v.split(" ")[0]})`;
    default: return v;
  }
}

/* renderingRows is what an approver is shown, as rows in its order: each
   line's label, its value in plain words, and the value as signed where the
   two differ. A row repeating the key of the row before carries no label. */
export function renderingRows(a, now = new Date()) {
  const rows = [];
  const [head, policies] = splitPolicies(a.rendering);
  for (const line of head.split("\n")) {
    if (!line) continue;
    const i = line.indexOf(": ");
    const key = i < 0 ? "" : line.slice(0, i), raw = i < 0 ? line : line.slice(i + 2);
    const plain = plainOf(key, raw, a, now);
    rows.push({ key, label: LABEL[key] || key, plain, raw: plain === raw ? "" : raw, repeat: rows.length > 0 && rows[rows.length - 1].key === key });
  }
  if (policies !== undefined) rows.push({ key: "policies", label: LABEL.policies, plain: policies, raw: "", repeat: false, block: true });
  return rows;
}

/* A rules rendering ends in a line "policies:" and the rules as written,
   which are text, not lines of key and value. */
function splitPolicies(rendering) {
  const lines = String(rendering || "").split("\n"), at = lines.indexOf("policies:");
  return at < 0 ? [lines.join("\n")] : [lines.slice(0, at).join("\n"), lines.slice(at + 1).join("\n")];
}

/* The lines a sentence about it needs, by key. */
function linesOf(a) {
  const out = {};
  for (const line of splitPolicies(a.rendering)[0].split("\n")) {
    const i = line.indexOf(": ");
    if (i > 0) (out[line.slice(0, i)] = out[line.slice(0, i)] || []).push(line.slice(i + 2));
  }
  return out;
}
const isList = (a) => a.kind === "approvers";
const isRules = (a) => a.kind === "rules";
const rulesWords = (l) => `version ${l.version?.[0] || "?"}${l.mode ? `, ${l.mode[0] === "enforce" ? "enforced" : "in log mode"}` : ""}${l.expires && valid(l.expires[0]) ? `, running until ${day(l.expires[0])}` : ""}`;

/* headline is what is about to happen, in one sentence from what the
   approver is shown, the place first so it never reads as part of the
   change. */
export function headline(a) {
  const l = linesOf(a);
  if (isList(a)) {
    const names = (l.approver || []).map((v) => person(v.split(" ")[0]));
    return `A new approver list for ${a.customer}${names.length ? `, naming ${list(names)}` : ""}.`;
  }
  if (isRules(a)) return `New rules for every box of ${a.customer}: ${rulesWords(l)}.`;
  const changes = (l.entry || []).map(parseEntry);
  const who = l.agent ? signerWords(l.agent[0]) : "an agent";
  const what = changes.length === 1 ? `make ${changeWords(changes[0])}` : changes.length > 1 ? `make ${changes.length} kinds of change` : "make a change";
  const until = l.until && valid(l.until[0]) ? `, until ${hm(l.until[0])}` : "";
  const asks = `${subject(who)} asks to ${what}${until}.`;
  return l.box ? `At ${list(l.box)}, ${asks}` : capArticle(asks);
}

/* brief is the Needs you strip's line, as the Trust Fabric's: where, and
   what; the page says the rest. */
export function brief(a) {
  const l = linesOf(a);
  if (isList(a)) return { where: a.customer, what: "A new approver list" };
  if (isRules(a)) return { where: a.customer, what: cap(`new rules, ${rulesWords(l)}`) };
  const changes = (l.entry || []).map(parseEntry);
  const what = changes.length === 1 ? cap(changeWords(changes[0])) : changes.length > 1 ? `${changes.length} kinds of change` : "A change";
  return { where: l.box ? list(l.box) : a.customer, what };
}

/* ── Where an approval stands ──────────────────────────────────────────── */

export function timeLeft(iso, now = new Date()) {
  if (!valid(iso)) return "";
  const m = Math.floor((Date.parse(iso) - now.getTime()) / 60000);
  return m < 0 ? "out of time" : m < 1 ? "under a minute left" : `${plural(m, "minute", "minutes")} left`;
}

const yeses = (a) => a.approvals || [];
export const saidYes = (a, you) => !!you && yeses(a).some((y) => y.by === you);

export function stateOf(a, now = new Date()) {
  switch (a.status) {
    case "waiting": {
      const left = timeLeft(a.expires, now), need = a.needs || 1;
      const so = need > 1 ? `Has ${yeses(a).length} of the ${need} yeses it needs, from ${need} different people` : "Waiting for a yes";
      return { tone: "wait", text: left ? `${so} · ${left}` : so };
    }
    case "released": return { tone: "ok", text: "Approved" };
    case "declined": return { tone: "bad", text: "Declined" };
    case "expired": return { tone: "idle", text: "Ran out of time" };
    case "withdrawn": return { tone: "idle", text: "Withdrawn" };
    default: return { tone: "idle", text: a.status || "" };
  }
}

export function outcome(a, now = new Date()) {
  const said = yeses(a).map((y) => `${person(y.by)} at ${at(y.at, now)}`);
  const no = isList(a) ? "The list stays as it is." : isRules(a) ? "The rules stay as they are." : "The agent may not make this change.";
  switch (a.status) {
    case "released":
      return `${list(said)} said yes. ${isList(a) ? "Every box takes the new list when it next hears from the console."
        : isRules(a) ? "Every box takes the new rules when it next hears from the console."
          : "The change may go ahead, and every box checks each yes again before it does."}`;
    case "declined": {
      const why = a.why && a.why !== "no reason given" ? `: “${a.why}”` : "";
      return `${a.declined_by ? person(a.declined_by) : "An approver"} declined${valid(a.ended) ? ` at ${at(a.ended, now)}` : ""}${why}. ${no}`;
    }
    case "expired": return `Nobody said yes in time. ${no}`;
    case "withdrawn": return `It was withdrawn${a.why ? `: ${a.why}` : ""}, so nothing waits for you.`;
    default: return said.length ? `${list(said)} said yes so far.` : "";
  }
}

/* What this person may do with it, when it is not simply to decide. */
export function yourPart(view, now = new Date()) {
  const a = view.approval, mine = yeses(a).find((y) => y.by === view.you);
  if (mine) return `You said yes at ${at(mine.at, now)}.${a.status === "waiting" ? " It needs a yes from someone else too." : ""}`;
  if (a.status === "waiting" && !view.approver) return `You are not on ${a.customer}’s approver list as its boxes hold it, so your yes would not count.`;
  return "";
}

/* Who asked, and why: the agent the rendering names, and the investigation
   it asked for, in the console's own words. */
export function askedBy(a, task, now = new Date()) {
  const when = valid(a.asked) ? ` at ${at(a.asked, now)}` : "";
  if (isList(a)) return `An administrator of ${a.customer} asked${when}, to put the people who added passkeys on the list.`;
  if (isRules(a)) return `An administrator of ${a.customer} asked${when}, to put new rules in force.`;
  const l = linesOf(a);
  const who = l.agent ? capArticle(signerWords(l.agent[0])) : "An agent";
  const why = task?.question ? `, for the investigation “${task.question}”${task.by ? `, which ${taskBy(task.by)} started` : ""}` : "";
  return `${subject(who)} asked${when}${why}.`;
}

/* The fatigue note: how often this person said yes in the last hour, and a
   warning once the server flags it as more than a person weighs. */
export function fatigueNote(f) {
  const k = f?.last_hour || 0;
  if (f?.fatigued) return { warn: true, text: `You have said yes ${times(k)} in the last hour. When yeses come this fast they stop being read: read every row before you say yes again.` };
  return { warn: false, text: k ? `You have said yes ${times(k)} in the last hour.` : "You have not said yes to anything else in the last hour." };
}
export const DECEIVED = "A passkey stops a forged approval, not a deceived one.";
export const DECEIVED_WHY = "Nobody can say yes as you without your device, but your passkey signs whatever you agree to.";

export const APPROVE = "Approve with passkey";
export const APPROVING = "Waiting for your passkey…";
export const DECLINE = "Decline";
export const WHAT_YOU_APPROVE = "What you approve";
export const WHO_ASKS = "Who asks, and why";
export const ASKED_NOT_SIGNED = "None of this is part of what you approve: only the rows above are.";
export const SIGNS = "What your passkey signs";
export const signsLead = (a) => `Your passkey signs two fingerprints and nothing else: one of the ${isList(a) ? "approver list" : isRules(a) ? "rules" : "key card"}, and one of the rows above, exactly as written below. Every box works both out again by itself and refuses a yes for anything else.`;
export const CARD_PRINT = "Fingerprint of what is approved";
export const ROWS_PRINT = "Fingerprint of the rows";
export const ROWS_CHECKED = "This page worked it out from the rows it shows, and it matches.";
export const ROWS_AS_SIGNED = "The rows exactly as signed";

export const MISMATCH = "This page did not ask your passkey: the server asked it to sign something other than the rows shown here. Nothing was approved. Tell Weavian.";
export const SHOWN_MISMATCH = "The rows shown are not the ones the server fingerprinted, so this page did not ask your passkey. Nothing was approved. Tell Weavian.";
/* Why the page cannot use a passkey, by passkeys.passkeyBlocker's answer. */
export const BLOCKED = {
  framed: "This page is shown inside another page, and every box refuses a yes given there. Open the console in its own window.",
  insecure: "This page is not on a secure address, so it cannot use a passkey or check what it shows. Open the console at its https address.",
  unsupported: "This browser cannot use passkeys. Open the console in a current browser.",
};
export const passkeyError = (e, making) => ({
  NotAllowedError: making ? "No passkey was made: it was cancelled or took too long." : "Your passkey was not used: it was cancelled or took too long. Nothing was approved.",
  InvalidStateError: "This device already holds a passkey of yours for this console.",
  SecurityError: "The browser refused: this console’s address is not the one passkeys are made for here.",
  NotSupportedError: "This device has no kind of passkey your boxes can check.",
}[e?.name] || e?.message || "The passkey did not answer.");

export const NEEDS_YOU = "Needs you";
export const LOOK = "Look at it";
export const andMore = (k) => `And ${plural(k, "more change", "more changes")} waiting for your yes`;
export const NOTHING_WAITS = "Nothing waits for your yes. When an AI agent asks for a change that needs one, it appears here and at the top of every screen.";
export const BACK_TO_ALL = "Everything waiting for you";

/* ── Approvers ─────────────────────────────────────────────────────────── */

export const APPROVERS_LEAD =
  "An approver says yes before an AI agent may make a big change: one yes for a change that reaches a site’s connection, " +
  "and two, from two different people, for one that reaches more than one site. They say it with a passkey on their own " +
  "phone or computer, and every box checks each yes itself before it changes anything.";
export const YOUR_PASSKEYS = "Your passkeys";
export const PASSKEYS_LEAD = "A passkey stays on your phone or computer and opens with your face, fingerprint or PIN. Nobody can say yes as you without it.";
export const NO_PASSKEYS = "You have no passkey here yet.";
export const passkeyLine = (p, now = new Date()) =>
  `Added ${day(p.added)}, ${valid(p.used) ? `last used ${at(p.used, now)}` : "not used yet"}. ${p.on_list ? "On the list your boxes hold." : "Not on the list yet: it counts once a new list names it."}`;
export const PASSKEY_NAME = "Name it";
export const PASSKEY_NAME_HINT = "Which device it is on, so you can tell your passkeys apart";
export const ADD_PASSKEY = "Add a passkey on this device";
export const ADDING = "Waiting for this device…";
export const added = (label) => `${label} is ready. It counts for a yes once your approver list names it.`;
export const mayNotRegister = (name) => `Only ${name}’s administrators and its approvers add passkeys to approve for it.`;
export const passkeysOff = (why) => `Passkeys are off on this console: ${why}.`;
/* A guess at the device's name, for the passkey's name field. */
export function deviceName(ua = navigator.userAgent) {
  const kind = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android phone"
    : /Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows computer" : "computer";
  return `This ${kind}`;
}

const nameOf = (p) => p.user || person(p.name);
export function listSay(view, name) {
  const l = view.list;
  if (!l || !(l.approvers || []).length) return `Nobody approves for ${name} yet, so no agent may make a change on its boxes that needs a yes.`;
  const names = l.approvers.map(nameOf);
  const who = names.length === 1
    ? `${names[0]} is the only approver for ${name}, so no change that reaches more than one site can be approved until a second person joins.`
    : `${list(names)} approve for ${name}.`;
  const by = l.signed_by === "root" ? "Weavian’s root key signed the list" : "An approver on the list before approved this one";
  return `${who} ${by}${valid(l.not_before) ? ` on ${day(l.not_before)}` : ""}.`;
}
export const approverName = (p, you) => `${nameOf(p)}${p.name === you ? " (you)" : ""}`;
export const approverLine = (p) => [p.role && roleLabel(p.role), p.passkeys?.length ? plural(p.passkeys.length, "passkey", "passkeys") : "No passkey"].filter(Boolean).join(" · ");
/* The people who added passkeys and are not on the list yet. */
export function notYetListed(view) {
  const names = (view.registered || []).filter((h) => !h.on_list && h.passkeys?.length).map((h) => h.user);
  return names.length ? `${list(names)} added ${names.length === 1 ? "a passkey" : "passkeys"} and ${names.length === 1 ? "is" : "are"} not on the list yet.` : "";
}

/* Whether each box took the list in force, from what it last reported. */
export function boxHolds(b, l, now = new Date()) {
  const st = b.authority || {};
  const quiet = b.online === false ? " Not reporting now." : "";
  if (b.took) return { tone: "ok", text: `Holds it${valid(st.approvers_pinned_at) ? ` since ${day(st.approvers_pinned_at)}` : ""}.${quiet}` };
  if (l && st.pending?.digest === l.digest) return { tone: "idle", text: `Takes it at ${at(st.pending.until, now)}: a list Weavian’s offline root key signs waits 24 hours.${quiet}` };
  if (st.approvers_statement) return { tone: "idle", text: `Holds an earlier list, and takes this one when it next hears from the console.${quiet}` };
  return { tone: "idle", text: `Holds no list yet.${quiet}` };
}
export function heldBy(view) {
  const boxes = view.boxes || [], k = boxes.filter((b) => b.took).length;
  if (!boxes.length || !view.list) return "";
  return k === boxes.length ? (k === 1 ? "Its box holds it." : `All ${k} boxes hold it.`) : k ? `${k} of ${boxes.length} boxes hold it.` : "No box holds it yet.";
}
/* Where the list's passkeys work: the address they were made for and the
   console addresses they approve at, as the list names them. */
export const listWhere = (l) => (l.rp_id && l.origins?.length
  ? `Its passkeys are made for ${l.rp_id} and approve only at ${list(l.origins)}.` : "");
export const LIST_PRINT = "Fingerprint of the list";
export const listPrint = (l) => `List ${l.serial}${valid(l.not_before) ? `, from ${day(l.not_before)}` : ""}. Each box holds a list by this fingerprint.`;
export const CHANGE_NEEDS = "What a change to the list needs";
export const CHANGE_STEPS = [
  "Weavian’s offline root key signs your first list, from the passkeys added on this screen: ask Weavian to place it once everyone who approves has added one.",
  "After that, one approver already on the list is enough: their passkey’s yes to a new list, and every box takes it as soon as it hears.",
  "A new list Weavian’s offline root key signs instead, when nobody on the list can say yes any more, waits 24 hours on every box before it is taken.",
];

/* ── Agent rules and the freeze ────────────────────────────────────────── */

/* inForce is the rules bundle in force, from GET /mesh/rules: the newest
   kept, since its mode began (the server's log_since for log mode; for
   enforcement, when the newest run of enforced versions began). Null when
   the customer has none of its own. */
export function inForce(view) {
  const bundles = view.bundles || [];
  if (!bundles.length) return null;
  const b = bundles[0];
  if (b.mode !== "enforce") return { ...b, since: view.log_since };
  let since = b.signed;
  for (const x of bundles) {
    if (x.mode !== "enforce") break;
    since = x.signed;
  }
  return { ...b, since };
}
/* What waits for an approver's yes among the rules. */
export const rulesWaiting = (view) => ((view.waiting || []).length
  ? `${list(view.waiting.map((p) => `Version ${linesOf(p).version?.[0] || "?"}`))} ${view.waiting.length === 1 ? "waits" : "wait"} for an approver’s yes.` : "");

/* The rules bundle in force: undefined when the server lists none, null
   when the customer has none of its own. */
export function bundleSay(b, now = new Date()) {
  if (b === undefined) return { tone: "idle", text: "This server does not list your rules yet: each box below says what it holds." };
  if (!b) return { tone: "idle", text: "No rules of your own yet: every box judges agents by Weavian’s defaults, which your rules can only narrow." };
  const mode = b.mode === "enforce"
    ? `enforced${valid(b.since) ? ` since ${day(b.since)}` : ""}: every box refuses what they do not allow.`
    : `in log mode${valid(b.since) ? ` since ${day(b.since)}` : ""}: every box writes down what they would refuse, and refuses nothing yet.`;
  const ran = valid(b.expires) && Date.parse(b.expires) <= now.getTime();
  const ends = !valid(b.expires) ? ""
    : ran ? ` They ran out on ${day(b.expires)}, so no agent may change anything until a new version arrives.`
      : ` They run until ${day(b.expires)}. After that no agent may change anything until a new version arrives.`;
  return { tone: ran ? "bad" : "ok", text: `Your rules are version ${b.version}, ${mode}${ends}` };
}
export const LOG_THEN_ENFORCE = "An approver switches on enforcement, after at least 14 days in log mode.";
export const READ_RULES = "Read the rules";
export const RULES_PRINT = "Fingerprint of the rules";

/* The freeze in force, from the newest the server issued. */
export function freezeSay(frozen, now = new Date()) {
  if (!frozen) return { tone: "ok", text: "Not frozen: agents may change what their key cards and your rules allow." };
  const f = frozen.freeze;
  const why = f.why ? ` Why: “${f.why}”.` : "";
  const until = valid(f.until) ? ` Until ${clock(f.until, now)}.` : "";
  return { tone: "bad", text: `Frozen since ${at(f.at, now)}${frozen.by ? ` by ${frozen.by}` : ""}: every box refuses every change an agent asks for. Agents can still read.${why}${until}` };
}

/* What a box holds of the rules, and whether it took the freeze in force,
   from what it last reported. */
export function boxRules(b, frozen, now = new Date()) {
  const r = b.rules || {};
  const quiet = b.online === false ? " Not reporting now." : "";
  const holds = r.closed ? `Refuses every agent change: ${r.closed}`
    : r.version ? `Version ${r.version}, ${r.mode === "enforce" ? "enforced" : "log mode"}${r.expired ? ", run out" : ""}`
      : b.rules ? "Weavian’s defaults only" : "Has not said which rules it holds";
  const took = frozen && r.freeze && r.freeze.seq >= frozen.freeze.seq && r.freeze.in_force;
  if (frozen && took) return { tone: "bad", text: `${holds}. Frozen${valid(r.freeze.taken_at) ? ` since ${at(r.freeze.taken_at, now)}` : ""}.${quiet}` };
  if (frozen) return { tone: "idle", text: `${holds}. Freezes when it next hears from the console.${quiet}` };
  return { tone: r.closed || r.expired ? "bad" : b.online === false || !b.rules ? "idle" : "ok", text: `${holds}.${quiet}` };
}

export const FREEZE = "Freeze every agent change";
export const askFreeze = (name, boxes) =>
  `Freeze every agent change at ${name}? ${boxes === 1 ? "Its box" : `All ${boxes} of its boxes`} will refuse every change an agent asks for, ` +
  "until someone here unfreezes. Agents can still read, and people can still make changes from this console.";
export const FREEZE_WHY = "Why, for every box’s logbook";
export const FREEZE_NOW = "Freeze now";
export const NOT_NOW = "Cancel";
export const UNFREEZE = "Unfreeze";
export const askUnfreeze = (name) => `Unfreeze ${name}? Agents may again change what their key cards and your rules allow.`;
export const UNFREEZE_NOW = "Unfreeze now";
export const KEEP_FROZEN = "Keep frozen";
export const ADMINS_ONLY = "Only your organisation’s administrators can freeze or unfreeze.";
export const NO_CUSTOMERS = "You see no organisation yet, so there is nothing here for you.";
